"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useFindPickupPoints = void 0;
var _react = require("react");
var _useDebounce = require("use-debounce");
var _useInstantSearchPickupPointsQuery = require("../../api/useInstantSearchPickupPointsQuery");
var _constants = require("../../constants");
var _SearchConfigurationProvider = require("../../containers/SearchConfigurationProvider");
var _hooks = require("../../hooks");
var _logger = require("../../logger");
var _models = require("../../models");
var _utils = require("../../utils");
const hookName = 'useFindPickupPoints';
// TODO: Remove after flat selection UI is used in all markets
/**
 * @obsolete Can be dropped later when flat selection UI is used in all markets
 * and/or the merged map & list UI switch is enabled everywhere.
 */
const useFindPickupPoints = findPickupPointsProps => {
  (0, _logger.logDebug)(`${hookName} hook called.\n\tProps:`, findPickupPointsProps);
  const {
    onDeliveryItemActivation,
    isMapEnabled,
    togglePickupPointsDialog,
    selectedDeliveryChannel
  } = findPickupPointsProps;
  const {
    isAddingNewDelivery
  } = (0, _hooks.useAnalytics)();
  const {
    filters,
    isInstantResultShown,
    maxDisplayedSearchResults
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  const defaultFilterValue = filters[0] ?? _models.DeliveryChannelsFilter.All;
  const breakpointIndex = (0, _hooks.useBreakpointIndex)();
  const [searchInputValue, setSearchInputValue] = (0, _react.useState)('');
  const [searchTerm, setSearchTerm] = (0, _react.useState)('');
  const [instantSearchTerm, setInstantSearchTerm] = (0, _react.useState)('');
  const [selectedLayout, setSelectedLayout] = (0, _react.useState)(_models.PickupPointsLayout.List);
  const [pickupPointsListType, setPickupPointsListType] = (0, _react.useState)(_models.PickupPointsListType.List);
  const [isInstantResultOpened, setIsInstantResultOpened] = (0, _react.useState)(false);
  const [currentListPage, setCurrentListPage] = (0, _react.useState)(0);
  const [activeFilter, setActiveFilter] = (0, _react.useState)(defaultFilterValue);
  const [debouncedInstantSearchTerm] = (0, _useDebounce.useDebounce)(instantSearchTerm, _constants.DEBOUNCE_TIMEOUT);
  const [selectedMapPickupPoint, setSelectedMapPickupPoint] = (0, _react.useState)(selectedDeliveryChannel);
  const [instantResultChannelId, setInstantResultChannelId] = (0, _react.useState)(undefined);
  const isMapAvailable = isMapEnabled;
  const isMapLayoutShown = isMapAvailable && selectedLayout === _models.PickupPointsLayout.Map;
  const isMapVisible = isMapLayoutShown && !isInstantResultOpened;
  const isInstantResultEnabled = isInstantResultShown || isMapLayoutShown;
  const {
    instantResults,
    isInstantResultsLoading
  } = (0, _useInstantSearchPickupPointsQuery.useInstantSearchPickupPointsQuery)({
    filter: activeFilter,
    isActive: isInstantResultEnabled,
    isMapEnabled: isMapAvailable,
    // TO-DO: only base instant search query is being run for now when true
    isMapLayoutShown,
    searchTerm: debouncedInstantSearchTerm,
    skip: 0,
    top: maxDisplayedSearchResults
  });
  const onPickupPointItemConfirmation = (0, _react.useCallback)(selectedItem => {
    onDeliveryItemActivation(selectedItem);
    togglePickupPointsDialog();
  }, [onDeliveryItemActivation, togglePickupPointsDialog]);
  const handleBackClick = (0, _react.useCallback)(() => {
    (0, _logger.logDebug)('handleBackClick has been called.');
    if (isInstantResultOpened) {
      setIsInstantResultOpened(false);
      return;
    }
    togglePickupPointsDialog();
  }, [isInstantResultOpened, togglePickupPointsDialog]);

  /** Pre-selecting instant result item. Now works same way in both list & map tabs. */
  const selectInstantResult = (0, _react.useCallback)(pickupPoint => {
    setSelectedMapPickupPoint({
      ...pickupPoint,
      deliveryFee: pickupPoint.shippingFee,
      estimatedDelivery: pickupPoint.estimatedDeliveryDate
    });
    setInstantResultChannelId(pickupPoint.deliveryChannelId);
    setIsInstantResultOpened(false);
    if (isInstantResultShown) {
      setPickupPointsListType(_models.PickupPointsListType.NearestOptions);
    }

    // analytics
    (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.DeliverySelection, isAddingNewDelivery.current ? _models.MetaHierarchy.FirstSelection : _models.MetaHierarchy.EditSelection, _models.MetaHierarchy.New, _models.MetaHierarchy.PickUpPoint, isMapLayoutShown ? _models.MetaHierarchy.MapSearch : _models.MetaHierarchy.ListSearch, _models.MetaHierarchy.SelectInstantResult]);
    (0, _logger.logDebug)(`${hookName} - selectInstantResult(): Preselected instant result with deliveryChannelId ${pickupPoint.deliveryChannelId}.`);
  }, [isAddingNewDelivery, isInstantResultShown, isMapLayoutShown]);
  const selectFirstInstantResult = (0, _react.useCallback)(() => {
    if (instantResults[0]) {
      const channelId = instantResults[0]?.deliveryChannelId;
      (0, _logger.logDebug)(`${hookName} hook: Selecting first instant result, deliveryChannelId ${channelId} ...`);
      selectInstantResult(instantResults[0]);
    }
  }, [instantResults, selectInstantResult]);
  const handleLayoutChange = (0, _react.useCallback)((_, newValue) => {
    setSelectedLayout(newValue);
    if (newValue === _models.PickupPointsLayout.List) {
      // display "normal on enter" search results when switching to list tab
      setSearchTerm(searchInputValue);
    } else {
      // re-search in map tab via setting the 1st found instant result as selected
      selectFirstInstantResult();
    }
  }, [searchInputValue, selectFirstInstantResult]);
  const isPickupPointsListVisible = pickupPointsListType === _models.PickupPointsListType.List && !isInstantResultOpened && (!isMapAvailable || selectedLayout === _models.PickupPointsLayout.List);
  return {
    handleBackClick,
    handleLayoutChange,
    instantResultProps: {
      instantResultChannelId,
      isInstantResultOpened,
      instantResults,
      isInstantResultsLoading,
      isMapAvailable,
      isMapLayoutShown,
      onPickupPointItemConfirmation,
      pickupPointsListType,
      searchTerm: debouncedInstantSearchTerm,
      selectInstantResult
    },
    instantResults,
    isInstantResultEnabled,
    isInstantResultsLoading,
    isInstantResultOpened,
    isMapAvailable,
    isMapVisible,
    isMapLayoutShown,
    isPickupPointsListVisible,
    pickupPointsListProps: {
      activeFilter,
      currentListPage,
      isMapAvailable,
      onPickupPointItemConfirmation,
      searchTerm,
      setActiveFilter,
      setCurrentListPage
    },
    pickupPointsMapProps: {
      activeFilter,
      instantResultChannelId,
      instantSearchTerm,
      isMapVisible,
      onPickupPointItemConfirmation,
      selectedMapPickupPoint,
      setSelectedMapPickupPoint
    },
    searchFieldProps: {
      isInstantResultEnabled,
      isInstantResultOpened,
      isMapLayoutShown,
      searchInputValue,
      searchTerm,
      selectFirstInstantResult,
      setCurrentListPage,
      setInstantResultChannelId,
      setInstantSearchTerm,
      setIsInstantResultOpened,
      setPickupPointsListType,
      setSearchInputValue,
      setSearchTerm
    },
    searchFilterProps: {
      defaultValue: defaultFilterValue,
      filters,
      isLabelVisible: breakpointIndex > 0,
      onFilterChange: value => {
        setActiveFilter(value);
        // analytics
        (0, _utils.dispatchUserActionEvent)([_models.MetaHierarchy.DeliverySelection, isAddingNewDelivery.current ? _models.MetaHierarchy.FirstSelection : _models.MetaHierarchy.EditSelection, _models.MetaHierarchy.New, _models.MetaHierarchy.PickUpPoint, isMapLayoutShown ? _models.MetaHierarchy.MapSearch : _models.MetaHierarchy.ListSearch, _models.MetaHierarchy.UseFilter]);
      },
      value: activeFilter
    },
    selectedLayout,
    togglePickupPointsDialog
  };
};
exports.useFindPickupPoints = useFindPickupPoints;